import React, { useState, useEffect } from 'react';
import { getDisciplineConfig } from './Pills/DisciplinePill';
import { getProviderProfileImage } from '../../services/profile/profileImage.service';

export interface ProviderAvatarProps {
  /** Used to fetch the photo if photoUrl not provided */
  userId: string;
  /** 2-letter fallback initials (e.g. "JD") */
  initials: string;
  /** Discipline code for color lookup via getDisciplineConfig() */
  discipline: string;
  /** sm=h-8 w-8, md=h-9 w-9 (default), lg=h-10 w-10 */
  size?: 'sm' | 'md' | 'lg';
  /** 'initials' = Care Team style, 'badge' = Admin Users style */
  displayMode?: 'initials' | 'badge';
  /** Discipline abbreviation in badge mode ("RN", "PT", etc.) */
  badgeLabel?: string;
  /** Extra Tailwind classes forwarded to root element */
  className?: string;
  /** Full name for img alt text accessibility */
  name?: string;
  /** Pre-fetched URL — skips API call when provided */
  photoUrl?: string;
}

const SIZE_CLASSES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const ProviderAvatar: React.FC<ProviderAvatarProps> = ({
  userId,
  initials,
  discipline,
  size = 'md',
  displayMode = 'initials',
  badgeLabel,
  className = '',
  name,
  photoUrl,
}) => {
  const [fetchedPhotoUrl, setFetchedPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (photoUrl) {
      setFetchedPhotoUrl(photoUrl);
      setImgError(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setImgError(false);
    setFetchedPhotoUrl(null);

    getProviderProfileImage(userId).then(result => {
      if (!cancelled) {
        setFetchedPhotoUrl(result.imageUrl ?? null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, photoUrl]);

  const config = getDisciplineConfig(discipline);
  const sizeClass = SIZE_CLASSES[size];
  const activeUrl = !loading && !imgError && fetchedPhotoUrl ? fetchedPhotoUrl : null;

  if (activeUrl) {
    return (
      <img
        src={activeUrl}
        alt={name ?? initials}
        className={`${sizeClass} rounded-full object-cover border-2 ${config.accent}${className ? ` ${className}` : ''}`}
        onError={() => setImgError(true)}
      />
    );
  }

  if (displayMode === 'badge') {
    return (
      <div className={`${sizeClass} rounded-full ${config.bg} ${config.text} flex items-center justify-center font-semibold text-xs border-2 ${config.border}${className ? ` ${className}` : ''}`}>
        {badgeLabel ?? initials}
      </div>
    );
  }

  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center text-white font-medium ${config.bar}${className ? ` ${className}` : ''}`}>
      {initials}
    </div>
  );
};

export default ProviderAvatar;
